// Theme state: 'light' | 'dark' | 'system', persisted in localStorage under
// the same key the inline script in index.html reads at first paint. Module
// state (not React state) so the map code and React components share one
// source of truth, mirroring the pattern in lib/auth.tsx.
import { useSyncExternalStore } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "gaea-theme";

const media = window.matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<() => void>();

function readStored(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === "light" || raw === "dark" ? raw : "system";
  } catch {
    return "system";
  }
}

let mode: ThemeMode = readStored();

export function resolveTheme(m: ThemeMode = mode): ResolvedTheme {
  return m === "system" ? (media.matches ? "dark" : "light") : m;
}

function apply(): void {
  document.documentElement.classList.toggle("dark", resolveTheme() === "dark");
}

function notify(): void {
  apply();
  for (const l of listeners) l();
}

export function setThemeMode(next: ThemeMode): void {
  mode = next;
  try {
    if (next === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* persistence is best-effort */
  }
  notify();
}

media.addEventListener("change", () => {
  if (mode === "system") notify();
});

// Keep multiple tabs in step: another tab writing the key re-syncs this one.
window.addEventListener("storage", (e) => {
  if (e.key === STORAGE_KEY || e.key === null) {
    mode = readStored();
    notify();
  }
});

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The user's chosen mode ('system' until they explicitly pick one). */
export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(subscribe, () => mode);
}

/** What is actually on screen right now — 'system' resolved via matchMedia. */
export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, () => resolveTheme());
}
